import { lazy, Suspense } from 'react';
import { Navigate, BrowserRouter, Routes, Route } from 'react-router-dom';
import ProtectedRoute from './features/auth/ProtectedRoute';
import { ROUTES } from 'shared/routes/routes';
import ChunkErrorBoundary from 'shared/components/ChunkErrorBoundary';
import InstallAppBanner from 'shared/components/InstallAppBanner';
import LoadingCard from 'shared/ui/LoadingCard';

const SignupPage = lazy(() => import('./features/auth/signup/pages/SignupPage'));
const LoginPage = lazy(() => import('./features/auth/login/pages/LoginPage'));
const EvaluationAdminPage = lazy(() => import('./features/evaluation/pages/EvaluationAdminPage'));
const Dashboard = lazy(() => import('./features/dashboard/pages/Dashboard'));
const RankingPage = lazy(() => import('./features/ranking/pages/RankingPage'));
const SchedulePage = lazy(() => import('./features/schedule/pages/SchedulePage'));
const TrainingPage = lazy(() => import('./features/training/pages/TrainingPage'));
const CalendarPage = lazy(() => import('./features/calendar/pages/CalendarPage'));
const AccountPage = lazy(() => import('./features/account/pages/AccountPage'));
const AdminPage = lazy(() => import('./features/admin/pages/AdminPage'));
const AdminClientsPageV2 = lazy(() => import('./features/admin/pages/AdminClientsPageV2'));
const TrainingLibraryPageV2 = lazy(() => import('./features/schedule/pages/TrainingLibraryPageV2'));
const EvaluationDetailPage = lazy(() => import('./features/evaluation/pages/EvaluationDetailPage'));
const EvaluationHistoricPage = lazy(() => import('./features/evaluation/pages/EvaluationHistoricPage'));
const ScheduleAdminPage = lazy(() => import('./features/schedule/pages/ScheduleAdminPage'));
const AnnouncementAdminPage = lazy(() => import('./features/announcement/pages/AnnouncementAdminPage'));
const ForgotPasswordPage = lazy(() => import('features/account/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('features/account/pages/ResetPasswordPage'));
const FeedbackAdminPage = lazy(() => import('features/training/pages/FeedbackAdminPage'));
const FeedbackPage = lazy(() => import('features/training/pages/FeedbackPage'));
const TermsPage = lazy(() => import('features/legal/pages/TermsPage'));
const PrivacyPage = lazy(() => import('features/legal/pages/PrivacyPage'));

function App() {
  return (
    <BrowserRouter>
      <ChunkErrorBoundary>
      <Suspense fallback={<LoadingCard fullScreen />}>
      <Routes>
        <Route path={ROUTES.home} element={<Navigate to={ROUTES.signup} replace />} />

        <Route 
          path={ROUTES.signup}
          element={
            <ProtectedRoute publicRoute>
              <SignupPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.login}
          element={
            <ProtectedRoute publicRoute>
              <LoginPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.forgotPassword}
          element={
            <ProtectedRoute publicRoute>
              <ForgotPasswordPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.resetPassword}
          element={
            <ProtectedRoute publicRoute>
              <ResetPasswordPage />
            </ProtectedRoute>
          }
        />

        <Route 
          path={ROUTES.dashboard}
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.schedule}
          element={
            <ProtectedRoute>
              <SchedulePage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.evaluationDetail()}
          element={
            <ProtectedRoute>
              <EvaluationDetailPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.evaluationHistoric}
          element={
            <ProtectedRoute>
              <EvaluationHistoricPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.ranking}
          element={
            <ProtectedRoute>
              <RankingPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.calendar}
          element={
            <ProtectedRoute>
              <CalendarPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.training}
          element={
            <ProtectedRoute>
              <TrainingPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.account}
          element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.feedbacks}
          element={
            <ProtectedRoute >
              <FeedbackPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.adminFeedbacks}
          element={
            <ProtectedRoute requireAdmin>
              <FeedbackAdminPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.adminEditUser()}
          element={
            <ProtectedRoute requireAdmin>
              <AccountPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.adminHome}
          element={
            <ProtectedRoute requireAdmin>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.adminClients}
          element={
            <ProtectedRoute requireAdmin>
              <AdminClientsPageV2 />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.adminLibrary}
          element={
            <ProtectedRoute requireAdmin>
              <TrainingLibraryPageV2 />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.adminAnnouncements}
          element={
            <ProtectedRoute requireAdmin>
              <AnnouncementAdminPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.adminCreateEvaluation}
          element={
            <ProtectedRoute requireAdmin>
              <EvaluationAdminPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.adminUserSchedule()}
          element={
            <ProtectedRoute requireAdmin>
              <ScheduleAdminPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path={ROUTES.adminUserEvaluations()}
          element={
            <ProtectedRoute requireAdmin>
              <EvaluationHistoricPage />
            </ProtectedRoute>
          }
        />

        <Route path={ROUTES.terms} element={<TermsPage />} />
        <Route path={ROUTES.privacy} element={<PrivacyPage />} />

        <Route path={ROUTES.fallback} element={<Navigate to={ROUTES.home} />} />
      </Routes>
      </Suspense>
      <InstallAppBanner />
      </ChunkErrorBoundary>
    </BrowserRouter>
  );
}

export default App;